/**
 * Route guard. Renders children only for a live session.
 *
 * This is convenience, NOT security. Every protected read and write is
 * authorised server-side by functions/auth/middleware.js against the database;
 * bypassing this component gets you a page that renders and then 401s on every
 * request it makes. Do not let a check here stand in for one there.
 *
 * Used two ways: wrapped around an element, and — as routes.jsx does — as a
 * pathless layout route with the guarded routes nested inside it, so the guard
 * sits INSIDE AppShell rather than around it and the shell is not torn down
 * and rebuilt on the way between a public page and an admin one.
 *
 * Three levels, each narrower than the last:
 *
 *   (default)   a live session, nothing more
 *   venueAdmin  venue_admin or above AT :venueSlug, or platform staff
 *   platform    super_admin and above, for the /admin/* routes
 *
 * venueAdmin covers the venue console and the TV display. /dashboard,
 * /my-stats and /profile deliberately take the DEFAULT instead: they are a
 * player's own pages, and gating them on an admin role would lock every player
 * out of the two entries the shell draws for them under "Me".
 */

import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useAuth, useVenue } from '../context/AuthContext.jsx';

const PLATFORM_ROLES     = new Set(['platform_owner', 'super_admin']);
const VENUE_ADMIN_ROLES  = new Set(['venue_admin', 'venue_owner']);

export default function ProtectedRoute({ children, platform = false, venueAdmin = false }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const { venueSlug } = useParams();
  const venue = useVenue(venueSlug);

  // The mount refresh is still in flight. Redirecting now would bounce a
  // signed-in user to /login on every hard reload.
  if (isLoading) {
    return <div className="hd-empty">Loading…</div>;
  }

  if (!isAuthenticated) {
    // `from` lets /login send them back where they were aiming.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (platform && !PLATFORM_ROLES.has(user?.platformRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  // A platform role outranks any venue role, the same rule AppShell applies
  // when it decides whether to draw the admin nav — the two must agree, or a
  // link appears that its own route then refuses.
  if (venueAdmin) {
    const allowed = PLATFORM_ROLES.has(user?.platformRole)
                 || VENUE_ADMIN_ROLES.has(venue?.role);

    if (!allowed) return <Navigate to="/dashboard" replace />;
  }

  return children ?? <Outlet />;
}
